import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import PostCard from "@/components/PostCard";
import Pagination from "@/components/Pagination";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";
import { ENGINE_OPTIONS, DEV_STATUS_OPTIONS } from "@/lib/engines";

const INITIAL_TAG_LIMIT = 15;
const PER_PAGE = 10;

export default function PostsListingPage() {
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [engine, setEngine] = useState("");
  const [devStatus, setDevStatus] = useState("");
  const [loading, setLoading] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Tag library (server-side, materialized collection — top-N most used)
  const [tagLib, setTagLib] = useState([]); // [{name, count}]
  const [showAllTags, setShowAllTags] = useState(false);

  // Autocomplete suggestions — server-side, debounced
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestIdx, setSuggestIdx] = useState(-1);
  const [tagSuggest, setTagSuggest] = useState([]); // server tag matches
  const tagSearchSeqRef = useRef(0);
  const suggestBoxRef = useRef(null);

  // ---------- Data loaders ----------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: PER_PAGE };
      if (q) params.q = q;
      if (tag) params.tag = tag;
      if (engine) params.engine = engine;
      if (devStatus) params.dev_status = devStatus;
      const { data } = await api.get("/posts", { params });
      setPosts(data.posts || []);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total_count ?? data.total ?? 0);
      // Clamp page if backend reports fewer pages than the current one
      // (can happen when filters narrow the result set).
      if (data.total_pages && page > data.total_pages) {
        setPage(data.total_pages);
      }
    } catch (e) {
      console.error("Failed to load posts:", e);
    } finally { setLoading(false); }
  }, [q, tag, engine, devStatus, page]);

  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);
// Reset to page 1 whenever any filter/search changes.
  useEffect(() => { setPage(1); }, [q, tag, engine, devStatus]);
  // Top tags (used for the chip row + "show more")
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/tags", { params: { limit: 500 } });
        if (!cancel) setTagLib(data.tags || []);
      } catch (e) {
        console.error("Failed to load tag library:", e);
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Debounced server-side tag search for the autocomplete suggestions.
  useEffect(() => {
    const ql = (q || "").trim();
    if (!ql || ql.length < 2) { setTagSuggest([]); return; }
    const seq = ++tagSearchSeqRef.current;
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/tags/search", { params: { q: ql, limit: 6 } });
        if (seq !== tagSearchSeqRef.current) return;
        setTagSuggest(data.tags || []);
      } catch (e) {
        if (seq === tagSearchSeqRef.current) setTagSuggest([]);
      }
    }, 275);
    return () => clearTimeout(handle);
  }, [q]);

  // ---------- Combine title + server-tag suggestions ----------
  const suggestions = useMemo(() => {
    const ql = (q || "").trim().toLowerCase();
    if (!ql || ql.length < 2) return [];
    // Title matches (from currently-loaded list)
    const titleMatches = posts
      .filter((p) => (p.title || "").toLowerCase().includes(ql))
      .slice(0, 5)
      .map((p) => ({ kind: "title", label: p.title, value: p.title, slug: p.slug }));
    // Tag matches (server-side)
    const tagMatches = tagSuggest.map((t) => ({
      kind: "tag",
      label: `${t.name} (${t.count})`,
      value: t.name,
    }));
    return [...titleMatches, ...tagMatches];
  }, [q, tagSuggest, posts]);

  const selectSuggestion = (s) => {
    if (!s) return;
    if (s.kind === "tag") {
      setTag(s.value);
      setQ("");
    } else {
      setQ(s.value);
    }
    setShowSuggest(false);
    setSuggestIdx(-1);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (suggestBoxRef.current && !suggestBoxRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const displayTags = showAllTags ? tagLib : tagLib.slice(0, INITIAL_TAG_LIMIT);
  const hasMore = tagLib.length > INITIAL_TAG_LIMIT;

  return (
    <PageShell>
      <section className="px-6 lg:px-10 xl:px-14 pt-14 pb-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="font-mystic text-rose-400 text-sm">翻訳の書庫</div>
            <h1 className="font-display text-4xl font-black text-white tracking-tight">All TL Games</h1>
            <p className="text-zinc-400 mt-1 text-sm">Browse every translation in the grimoire.</p>
          </div>

          {/* SEARCH with autocomplete */}
          <div className="relative w-full md:w-96" ref={suggestBoxRef}>
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              placeholder="Search by title or tag…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setShowSuggest(true); setSuggestIdx(-1); }}
              onFocus={() => setShowSuggest(true)}
              onKeyDown={(e) => {
                if (!showSuggest || suggestions.length === 0) return;
                if (e.key === "ArrowDown") { e.preventDefault(); setSuggestIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setSuggestIdx((i) => Math.max(i - 1, 0)); }
                else if (e.key === "Enter" && suggestIdx >= 0) { e.preventDefault(); selectSuggestion(suggestions[suggestIdx]); }
                else if (e.key === "Escape") { setShowSuggest(false); }
              }}
              className="pl-10 pr-10 bg-black/40 border-white/10"
              data-testid="posts-search-input"
            />
            {q && (
              <button
                onClick={() => { setQ(""); setShowSuggest(false); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {showSuggest && suggestions.length > 0 && (
              <div
                className="absolute z-[60] left-0 right-0 mt-1 max-h-80 overflow-y-auto rounded-md border border-white/10 bg-zinc-950/98 backdrop-blur shadow-2xl ring-1 ring-rose-900/30"
                data-testid="search-suggestions"
                role="listbox"
              >
                {suggestions.map((s, idx) => (
                  <button
                    key={`${s.kind}-${s.value}-${idx}`}
                    onClick={() => selectSuggestion(s)}
                    onMouseEnter={() => setSuggestIdx(idx)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${idx === suggestIdx ? "bg-rose-600/20 text-white" : "text-zinc-200 hover:bg-white/5"}`}
                    data-testid={`suggest-${s.kind}-${s.value}`}
                  >
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${s.kind === "tag" ? "bg-rose-500/20 text-rose-300" : "bg-violet-500/20 text-violet-300"}`}>
                      {s.kind}
                    </span>
                    <span className="truncate">{s.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ENGINE + STATUS FILTERS */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Engine:</span>
            <button
              type="button"
              onClick={() => setEngine("")}
              data-testid="filter-engine-all"
              className={`px-2.5 py-1 rounded-full text-[11px] border transition ${engine === "" ? "bg-rose-600 border-rose-500 text-white" : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"}`}
            >
              All
            </button>
            {ENGINE_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setEngine(engine === o.value ? "" : o.value)}
                data-testid={`filter-engine-${o.value}`}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition ${engine === o.value ? "bg-rose-600 border-rose-500 text-white" : `${o.chip} hover:opacity-90`}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Status:</span>
            <button
              type="button"
              onClick={() => setDevStatus("")}
              data-testid="filter-status-all"
              className={`px-2.5 py-1 rounded-full text-[11px] border transition ${devStatus === "" ? "bg-rose-600 border-rose-500 text-white" : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"}`}
            >
              All
            </button>
            {DEV_STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setDevStatus(devStatus === o.value ? "" : o.value)}
                data-testid={`filter-status-${o.value}`}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition ${devStatus === o.value ? "bg-rose-600 border-rose-500 text-white" : `${o.chip} hover:opacity-90`}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          {(engine || devStatus || tag) && (
            <button
              type="button"
              onClick={() => { setEngine(""); setDevStatus(""); setTag(""); }}
              data-testid="filter-clear"
              className="ml-auto px-2.5 py-1 rounded-full text-[11px] border bg-white/5 border-white/10 text-zinc-400 hover:bg-white/10"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* TAG LIBRARY */}
        {tagLib.length > 0 && (
          <div className="mb-8">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTag("")}
                data-testid="filter-all"
                className={`px-3 py-1 rounded-full text-xs border transition ${tag === "" ? "bg-rose-600 border-rose-500 text-white" : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"}`}
              >
                All
              </button>
              {displayTags.map((t) => (
                <button
                  key={t.name}
                  onClick={() => setTag(t.name === tag ? "" : t.name)}
                  data-testid={`filter-tag-${t.name}`}
                  className={`px-3 py-1 rounded-full text-xs border transition flex items-center gap-1.5 ${tag === t.name ? "bg-rose-600 border-rose-500 text-white" : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"}`}
                  title={`${t.count} post${t.count === 1 ? "" : "s"}`}
                >
                  <span>{t.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tag === t.name ? "bg-white/20 text-white" : "bg-black/30 text-zinc-400"}`}>{t.count}</span>
                </button>
              ))}
              {hasMore && (
                <button
                  onClick={() => setShowAllTags((v) => !v)}
                  data-testid="toggle-show-more-tags"
                  className="px-3 py-1 rounded-full text-xs border bg-violet-600/20 border-violet-500/40 text-violet-200 hover:bg-violet-600/30 transition"
                >
                  {showAllTags ? "Show Less" : `Show More (${tagLib.length - INITIAL_TAG_LIMIT})`}
                </button>
              )}
            </div>
          </div>
        )}

                {loading ? (
          <div className="text-center py-20 text-zinc-500 font-mystic">
            Channeling the archives…
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-zinc-500 font-mystic">
            No translations match your incantation.
          </div>
        ) : (
          <>
            <div className="grid gap-6 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
              {posts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>

            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(p) => {
                setPage(p);
                if (typeof window !== "undefined") {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            />

            {totalCount > 0 && (
              <div className="mt-3 text-center text-[11px] uppercase tracking-widest text-zinc-500">
                Page {page} of {totalPages} · {totalCount} total
              </div>
            )}
          </>
        )}
      </section>
    </PageShell>
  );
}
